import { createContext, createElement, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import type { AccountInfo, AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";

type AccessTokenContextValue = {
  accessToken: string | null;
  instance: IPublicClientApplication;
  accounts: AccountInfo[];
  // True once it's safe to fire authenticated requests: a token is in hand, OR
  // there's no MSAL account to get one for (local bypass / logged-out — nothing
  // to wait on). The app shell holds tab content until this is true so a tab
  // never mounts and fetches before acquireTokenSilent resolves — that cold-start
  // race is what produced 401 "Authorization header is missing" until a refresh.
  authReady: boolean;
};

const AccessTokenContext = createContext<AccessTokenContextValue | null>(null);

// A forcing condition (most often a Conditional Access sign-in-frequency window
// elapsing) makes acquireTokenSilent throw InteractionRequiredAuthError. When it
// does, MANY triggers can hit it at once — the renewal timer, a visibility change,
// and every in-flight authenticated fetch — and each USED to call
// acquireTokenRedirect independently, producing the "many forced logins in a few
// minutes" storm seen in the Entra sign-in logs. This module-level latch ensures
// AT MOST ONE interactive re-auth is launched per tab. It resets on a healthy
// silent renewal (see the provider effect) and naturally on the post-redirect
// page load.
let interactiveReauthInFlight = false;

// Launch a single guarded interactive re-auth. Exported so the provider AND
// useAuthFetch funnel through the SAME latch instead of racing each other into
// duplicate redirects. `redirectStartPage` returns the user to the exact page
// they were on so re-auth isn't disorienting.
export const beginInteractiveReauth = (
  instance: IPublicClientApplication,
  account: AccountInfo | null | undefined,
): void => {
  if (interactiveReauthInFlight || !account) return;
  interactiveReauthInFlight = true;
  void instance
    .acquireTokenRedirect({
      account,
      scopes: ["User.Read"],
      redirectStartPage: window.location.href,
    })
    .catch(() => {
      // The redirect failed to even start (rare) — clear the latch so a later
      // attempt isn't permanently blocked.
      interactiveReauthInFlight = false;
    });
};

// Acquires the Azure ID token ONCE for the whole app and shares it via context.
// Previously every tab called useAccessToken() independently, each starting at
// null and running its own acquireTokenSilent AFTER mount — so each tab's first
// render raced the token and fired requests with no Authorization header. Doing
// it once at the shell means every tab reads the token synchronously at mount.
// The token is renewed shortly BEFORE its real expiry (read from the MSAL
// result, not on a blind fixed interval) and on regaining tab visibility; Azure
// ID tokens live ~60-90 min and a stale one yields 403 "Token has expired".
export const AccessTokenProvider = ({ children }: { children: ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    if (accounts.length === 0 || inProgress !== InteractionStatus.None) return;

    // Prefer the persisted active account over accounts[0], whose ordering is
    // unstable once a second identity is cached.
    const account = instance.getActiveAccount() ?? accounts[0];
    const request = { account, scopes: ["User.Read"] };
    let cancelled = false;
    let renewTimer: number | undefined;

    // Renew this long BEFORE the token we send expires.
    const LEAD_MS = 120_000;

    // The expiry of the token we actually SEND — the ID token's own `exp`
    // claim. NOT `response.expiresOn`, which is the ACCESS token's expiry.
    //
    // They are different tokens with different lifetimes: Entra jitters the
    // access token between 60 and 90 minutes while the ID token gets ~60.
    // Observed live on 2026-09-10: ID exp 10:59:25, access exp 11:21:25 — a
    // 22-minute gap. Keying the renewal off `expiresOn` scheduled it for
    // 11:19:25, twenty minutes after the bearer had died, and every static-
    // pattern caller 403'd "Token has expired" in between. Nobody waits twenty
    // minutes; they log out. That was the "few times a day" forced re-login.
    const idTokenExpiry = (response: AuthenticationResult): Date | null => {
      const exp = (response.idTokenClaims as { exp?: number } | undefined)?.exp;
      return typeof exp === "number" ? new Date(exp * 1000) : null;
    };

    const scheduleNext = (idExpiresOn: Date | null) => {
      // Renew LEAD_MS before the ID token's real expiry (from its own claims)
      // rather than on a blind 10-min tick that can straddle expiry. Clamp to
      // [30s, 30min] so a bad/huge value can neither stall nor hammer renewal;
      // an early clamped fire just re-reads the cache and re-arms.
      const leadMs = idExpiresOn ? idExpiresOn.getTime() - Date.now() - LEAD_MS : 10 * 60_000;
      const delay = Math.min(Math.max(leadMs, 30_000), 30 * 60_000);
      // The scheduled renewal MUST force. MSAL decides whether to refresh by
      // looking at the ACCESS token, so without forceRefresh it returns the same
      // cached bundle — expired ID token included — and calls it a success.
      renewTimer = window.setTimeout(() => refresh(true), delay);
    };

    const refresh = (force = false) => {
      instance
        .acquireTokenSilent({ ...request, forceRefresh: force })
        .then((response) => {
          if (cancelled) return;
          const idExp = idTokenExpiry(response);
          // An unforced call (mount, tab-visible) may legitimately get the
          // cached bundle back. If the ID token in it is already inside the
          // lead window — or dead — that bundle is exactly what is wrong.
          // Force once, immediately, rather than putting a stale token in state.
          if (!force && idExp !== null && idExp.getTime() - Date.now() < LEAD_MS) {
            refresh(true);
            return;
          }
          // Send the ID token, NOT the Graph access token: the ID token
          // (aud = our client id, v2.0 issuer, no header nonce) verifies against
          // the public JWKS, whereas the Graph token is signed specially and the
          // backend's verify_azure_jwt would reject it.
          setAccessToken(response.idToken);
          interactiveReauthInFlight = false; // silent renewal is healthy again
          // Fall back to expiresOn only if the claims somehow carried no exp —
          // the old (wrong-token) behaviour is still better than never renewing.
          scheduleNext(idExp ?? response.expiresOn);
        })
        .catch((error) => {
          if (cancelled) return;
          if (error instanceof InteractionRequiredAuthError) {
            // Genuine interaction needed — launch AT MOST ONE redirect via the
            // shared latch, never a storm of them.
            beginInteractiveReauth(instance, account);
          } else {
            // Transient (network blip, monitor_window_timeout) — do NOT log the
            // user out; just try again on the normal cadence.
            scheduleNext(null);
          }
        });
    };

    refresh();

    // Re-check when the tab becomes visible again — DEBOUNCED and via a single
    // listener. (Was window 'focus' + document 'visibilitychange', which both
    // fired on every tab switch and multiplied the redirect storm.)
    let focusDebounce: number | undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      window.clearTimeout(focusDebounce);
      focusDebounce = window.setTimeout(refresh, 1000);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearTimeout(renewTimer);
      window.clearTimeout(focusDebounce);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accounts, inProgress, instance]);

  // Ready once MSAL has settled AND either there's no account to acquire for
  // (local bypass / logged-out) or the token has landed.
  const authReady = inProgress === InteractionStatus.None && (accounts.length === 0 || accessToken !== null);

  const value = useMemo(
    () => ({ accessToken, instance, accounts, authReady }),
    [accessToken, instance, accounts, authReady],
  );
  return createElement(AccessTokenContext.Provider, { value }, children);
};

// Consume the shared token. Same shape callers already destructure
// ({ accessToken, instance, accounts }) plus authReady. Must be rendered under
// <AccessTokenProvider> (mounted once at the app root in App.tsx).
export const useAccessToken = (): AccessTokenContextValue => {
  const ctx = useContext(AccessTokenContext);
  if (!ctx) {
    throw new Error("useAccessToken must be used within <AccessTokenProvider>");
  }
  return ctx;
};
